/**
 * Component Renderer Module - Main Export
 *
 * Provides a complete component rendering system for Webflow integration
 * that shows/hides pre-placed components based on scenario codes.
 *
 * Key Features:
 * - Configuration-driven component visibility
 * - Environment-based settings (development vs production)
 * - Polling for real-time scenario updates
 * - TypeScript interfaces for type safety
 * - Graceful error handling and fallback scenarios
 */

// Export all types
export * from './types';

// Export all configuration
export * from './config';

// Export integration layer
export * from './integration';

// Export API polling infrastructure
export * from './api';

// Export core renderer
export * from './core';

// Export system initialization
export * from './system';

// Import for local use
import {
  COMPONENT_CONFIG,
  getScenarioMapping,
  getEnvironmentConfig,
  isValidScenario,
  getAllComponentIds,
  getFallbackComponents,
  validateConfiguration,
} from './config';

import type { ComponentSystemConfig } from './types';

// Export types for external consumption
export type {
  ComponentConfig,
  ComponentSystemConfig,
  ComponentRenderer,
  ScenarioMapping,
  EnvironmentConfig,
  ComponentRenderState,
  ComponentAction,
  ComponentVisibilityAction,
} from './types';

/**
 * Quick setup function for webpack integration
 *
 * @param environment - 'development' or 'production'
 * @returns Configuration object ready for renderer initialization
 */
export function createComponentConfig(environment: 'development' | 'production' = 'production') {
  const envConfig = getEnvironmentConfig(environment);
  if (!envConfig) {
    throw new Error(`Invalid environment: ${environment}`);
  }

  // Validate configuration before returning
  const validation = validateConfiguration();
  if (!validation.isValid) {
    console.error('Component configuration validation failed:', validation.errors);
    if (!COMPONENT_CONFIG.system.gracefulFailure) {
      throw new Error(`Configuration validation failed: ${validation.errors.join(', ')}`);
    }
  }

  return {
    config: COMPONENT_CONFIG,
    environment: envConfig,
    isValid: validation.isValid,
    errors: validation.errors,
  };
}

/**
 * Get scenario-specific component actions
 *
 * @param scenarioCode - The scenario code to process
 * @returns Object with components to show and hide
 */
export function getComponentActions(scenarioCode: number): {
  show: string[];
  hide: string[];
  isValid: boolean;
  useFallback: boolean;
} {
  const mapping = getScenarioMapping(scenarioCode);

  if (!mapping) {
    // Use fallback scenario
    const fallback = getFallbackComponents();
    return {
      show: fallback.show,
      hide: fallback.hide,
      isValid: false,
      useFallback: true,
    };
  }

  return {
    show: mapping.componentsToShow,
    hide: mapping.componentsToHide,
    isValid: true,
    useFallback: false,
  };
}

/**
 * Environment detection helper
 */
export function detectEnvironment(): 'development' | 'production' {
  // Check various environment indicators
  if (typeof window !== 'undefined') {
    // Browser environment
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.includes('dev') || hostname.includes('staging')) {
      return 'development';
    }
  }

  // Node environment
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.NODE_ENV === 'development' || process.env.ENVIRONMENT === 'development') {
      return 'development';
    }
  }

  // Default to production for safety
  return 'production';
}

/**
 * Hide all outcome components immediately on page load
 * This should be called as early as possible to prevent flickering
 */
export function hideAllOutcomeComponents(): void {
  if (typeof document === 'undefined') return;

  const componentSelectors = [
    '[data-component="accs-form-processing"]',
    '[data-component="accs-outcome-loan-only"]',
    '[data-component="accs-outcome-ds-loan"]',
    '[data-component="accs-outcome-fallback"]',
    '[data-component="accs-outcome-ds-only"]',
    '[data-component="accs-outcome-unqualified"]',
  ];

  componentSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.display = 'none';
      htmlElement.classList.add('component-hidden');
    });
  });
}

/**
 * Component system initialization helper
 */
export async function initializeComponentSystem(options: {
  environment?: 'development' | 'production';
  debug?: boolean;
  customConfig?: Partial<ComponentSystemConfig>;
} = {}) {
  const environment = options.environment || detectEnvironment();
  const debug = options.debug !== undefined ? options.debug : environment === 'development';

  if (debug) {
    console.log('🎯 Initializing ProDebtCo Component System', {
      environment,
      debug,
      customConfig: !!options.customConfig,
    });
  }

  try {
    const setup = createComponentConfig(environment);

    if (debug && !setup.isValid) {
      console.warn('⚠️ Configuration validation warnings:', setup.errors);
    }

    return {
      success: true,
      config: setup.config,
      environment: setup.environment,
      debug,
      errors: setup.errors,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';

    if (debug) {
      console.error('❌ Component system initialization failed:', errorMessage);
    }

    return {
      success: false,
      error: errorMessage,
      config: null,
      environment: null,
      debug,
    };
  }
}